/**
 * 線路建設コスト計算（共通ユーティリティ）
 *
 * @see docs/specifications/logic/common/phase_04_build_track.md Section 4.1
 */

import { getTileDefinition } from '../constants/tiles';

/**
 * タイルカテゴリ
 */
export type TileCategory = 'simple' | 'complexCoexist' | 'complexCrossing';

const CROSSING_TILES = ['GENTLE_STRAIGHT_CROSS', 'TWO_GENTLE_CROSS', 'TWO_STRAIGHT_CROSS'];
const COEXIST_TILES = ['TWO_GENTLE_COEXIST', 'LEFT_GENTLE_ACUTE', 'RIGHT_GENTLE_ACUTE', 'STRAIGHT_ACUTE'];

/**
 * タイルカテゴリを判定
 */
export function getTileCategory(tileType: string): TileCategory {
  if (CROSSING_TILES.includes(tileType)) return 'complexCrossing';
  if (COEXIST_TILES.includes(tileType)) return 'complexCoexist';
  return 'simple';
}

/**
 * 建設コスト計算に必要なHex情報
 */
export interface HexForCost {
  town: { id: string } | null;
  tile: { type: string } | null;
  terrain: string;
}

/**
 * 線路建設コストを計算
 *
 * ## コスト表
 *
 * ### 新規配置（Placing Track）
 * | タイルカテゴリ | 平地 | 川 | 山岳 |
 * |--------------|------|-----|------|
 * | Simple | $2 | $3 | $4 |
 * | Complex Coexist | $3 | $4 | $5 |
 * | Complex Crossing | $4 | $5 | $6 |
 * | Town | $1 + $1 × 出口数 |
 *
 * ### 置き換え（Replacing Track）
 * - Simple → Complex Crossing: $3
 * - Town: $3
 * - All others (単線→単線、複線→複線含む): $2
 *
 * @param hex - 配置するHex
 * @param placementType - 配置種別
 * @param tileType - タイル種別
 * @returns コスト
 */
export function calculateBuildCost(
  hex: HexForCost,
  placementType: 'place' | 'replace',
  tileType: string | null
): number {
  // 置き換え（単線→複線、単線→単線、複線→複線すべて含む）
  if (placementType === 'replace' && tileType) {
    // Town: $3
    if (hex.town) return 3;

    // Simple → Complex Crossing: $3
    if (hex.tile) {
      const existingCategory = getTileCategory(hex.tile.type);
      const newCategory = getTileCategory(tileType);
      if (existingCategory === 'simple' && newCategory === 'complexCrossing') {
        return 3;
      }
    }

    // All others: $2
    return 2;
  }

  // 新規配置
  if (!tileType) return 0;

  // Town: $1 + $1 × 出口数
  // 出口数 = connections.flat().length（例: STRAIGHT [[0,3]] → 2出口 → $3）
  if (hex.town) {
    const tileDef = getTileDefinition(tileType);
    if (tileDef) {
      const exitCount = tileDef.connections.flat().length;
      return 1 + exitCount;
    }
    return 3; // デフォルト（2出口の場合）
  }

  // 通常ヘクス: 基本コスト + 地形追加コスト
  const category = getTileCategory(tileType);
  const baseCost: Record<TileCategory, number> = {
    simple: 2,
    complexCoexist: 3,
    complexCrossing: 4,
  };
  const terrainAdditional: Record<string, number> = {
    plains: 0,
    river: 1,
    mountains: 2,
  };

  const base = baseCost[category];
  const additional = terrainAdditional[hex.terrain] ?? 0;

  return base + additional;
}
